/**
 * Old URLs under the tab shell (navigation-ia.md §4): `/now`, `/inbox`, `/overview`,
 * `/projects/*`, `/you/*`, `/rules`, `/releases`, `/w/*`, `/t/*` and every session URL redirect to
 * their new home, keeping deep links (`?i=`, `?intent=`, `?seq=`, `#anchor`). Targets that belong
 * to one project (a run, a workstream, a task, a decision from a push) open inside that project's
 * tab once its project is known from cached data; otherwise the fallback (Home, or the run's short
 * link) still opens the thing. The mapping itself is pure and table-tested (lib/ia.ts).
 *
 * With `?flags=-lanesTab` (the escape hatch) the old pages render instead.
 */
import { useLocation, useNavigate } from '@tanstack/react-router'
import { type ComponentType, useEffect } from 'react'
import { useInboxRows } from '../components/inbox/useInboxRows.js'
import { SessionView } from '../components/SessionView.js'
import { useProjectTabs } from '../components/tabs/useProjectTabs.js'
import { SkeletonRows } from '../components/ui.js'
import { isEnabled } from '../lib/flags.js'
import { type LegacyTarget, legacyTarget, resolvedTarget } from '../lib/ia.js'
import { defaultProject } from '../lib/last-project.js'
import { useRuns, useTask, useWorkstream } from '../lib/queries.js'

/** The old page, or (tab shell on) a redirect to its new home. */
export function legacyPage(Page: ComponentType): () => React.ReactNode {
  return function LegacyOrRedirect() {
    return isEnabled('lanesTab') ? <LegacyRedirect /> : <Page />
  }
}

function Go({ to }: { to: string }) {
  const nav = useNavigate()
  useEffect(() => {
    void nav({ href: to, replace: true })
  }, [nav, to])
  return null
}
const go = (to: string) => <Go to={to} />

export function LegacyRedirect() {
  const loc = useLocation()
  const hash = loc.hash ? `#${loc.hash.replace(/^#/, '')}` : ''
  const t = legacyTarget(loc.pathname, loc.searchStr, hash)
  // Already canonical (this component re-renders once more while its route unmounts).
  if (!t) return null
  if ('to' in t) return go(t.to)
  switch (t.resolve) {
    case 'run':
      return <ResolveRun t={t} />
    case 'workstream':
      return <ResolveWorkstream t={t} />
    case 'task':
      return <ResolveTask t={t} />
    case 'inspect':
      return <ResolveInspect t={t} />
    case 'lastProject':
      return <ResolveLastProject t={t} />
  }
}

type Resolvable<K> = Extract<LegacyTarget, { resolve: K }>

function ResolveRun({ t }: { t: Resolvable<'run'> }) {
  const runs = useRuns()
  const run = runs.data?.find((r) => r.runId === t.id)
  if (run?.projectId) return go(resolvedTarget(t, run.projectId))
  if (runs.isPending) return <SkeletonRows n={4} />
  // Unknown project (imported history, another account's run): the viewer on its short link.
  return (
    <div className="lk-page is-run">
      <SessionView key={t.id} runId={t.id} />
    </div>
  )
}

function ResolveWorkstream({ t }: { t: Resolvable<'workstream'> }) {
  const ws = useWorkstream(t.id)
  if (ws.isPending) return <SkeletonRows n={4} />
  return go(resolvedTarget(t, ws.data?.projectId))
}

function ResolveTask({ t }: { t: Resolvable<'task'> }) {
  const task = useTask(t.id)
  if (task.isPending) return <SkeletonRows n={4} />
  return go(resolvedTarget(t, task.data?.projectId))
}

/** `/inbox?i=decision:<id>` (push deep links): the decision in its project's tab. */
function ResolveInspect({ t }: { t: Resolvable<'inspect'> }) {
  const built = useInboxRows()
  const [kind, id] = [t.key.slice(0, t.key.indexOf(':')), t.key.slice(t.key.indexOf(':') + 1)]
  const row = built.rows.find((r) => r.key === t.key) ?? built.snoozed.find((r) => r.key === t.key)
  const projectId =
    row?.projectId ??
    (kind === 'decision' ? built.decisions.data?.find((d) => d.id === id)?.projectId : undefined) ??
    undefined
  if (projectId) return go(resolvedTarget(t, projectId))
  if (built.loading) return <SkeletonRows n={4} />
  return go(t.fallback)
}

function ResolveLastProject({ t }: { t: Resolvable<'lastProject'> }) {
  const tabs = useProjectTabs()
  if (tabs.projects.isPending) return <SkeletonRows n={4} />
  const id = defaultProject(tabs.pinned.map((p) => p.id)) ?? tabs.projects.data?.[0]?.id
  return go(resolvedTarget(t, id))
}
